import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button, LinkButton } from "../../../../components/Button";
import Input from "../../../../components/Form/Input/input";
import { Form, FormGroup } from "../../../../components/Form/styles";
import { Box, Container as BarContainer, StickySpacer } from "../../../../components/SaveBar/styles";
import { ScreenHeader } from "../../../../components/SubHeader";
import { AppState, useAppState } from "../../../../contexts/App";
import { useDraftSitePreview } from "../../../../contexts/PreviewSite";
import { useSite } from "../../../../contexts/Site";
import { Site } from "../../../../model/site";
import { api } from "../../../../services/api";
import { ERR_FREE_SITES_LIMIT_REACHED, isApiError } from "../../../../services/api/errors";
import { isPlusRequiredCreation, markPlusRequiredCreation } from "../../../../utils/plusRequiredCreation";
import CategoriesInputSelect, { isCategoryComplete } from "../../components/CategoriesInputSelect";
import CreationProgress from "../../components/CreationProgress";
import { Card, Container, Section } from "./styles";

const TitlePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromAd = searchParams.get("flow") === "ads";

  const { appState, setAppState } = useAppState();
  const { site, setSite } = useSite();

  const [title, setTitle] = useState<string>(site?.title || "");
  const [category, setCategory] = useState<string>(site?.category || "");
  const [categoryOther, setCategoryOther] = useState<string>(site?.categoryOther || "");
  // From an ad page with a niche, the field of work is already known: only the name is asked for
  const [categoryKnown] = useState(fromAd && !!site?.category);

  useDraftSitePreview(
    useMemo(
      () => (site ? ({ ...site, title, category, categoryOther } as Site) : undefined),
      [site, title, category, categoryOther]
    )
  );

  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const saveHandler = async () => {
    const siteId = appState.editingSiteId as string;
    const copySite = { ...site, title, category, categoryOther: categoryOther.trim() || undefined } as Site;
    const draftOf = (): Site =>
      isPlusRequiredCreation(siteId) ? { ...copySite, intendedSubscription: "PLUS" } : copySite;

    const copyAppState = {
      ...appState,
      editingSiteModified: true,
    } as AppState;

    setIsSaving(true);
    setErrorMessage(undefined);

    try {
      // This is the first draft save for a brand-new Keepo: it is what actually
      // creates the site row on the server, so it is where the free Keepos limit
      // is enforced. Block navigation until it succeeds. Past the free limit, the Keepo is created as
      // PLUS instead (subscribing is asked for, and explained, when publishing)
      try {
        await api.saveDraft(siteId, draftOf());
      } catch (e) {
        if (!isApiError(e, ERR_FREE_SITES_LIMIT_REACHED) || isPlusRequiredCreation(siteId)) {
          throw e;
        }
        markPlusRequiredCreation(siteId);
        await api.saveDraft(siteId, draftOf());
      }

      setSite(copySite);
      setAppState(copyAppState);

      // From an ad page it goes straight to "Criar com IA". Otherwise a new Keepo picks how to start next:
      // empty or from a template
      navigate(fromAd ? "/editor/ai" : "/editor/start");
    } catch {
      setErrorMessage(t("pages.editor.title.errors.generic"));
      setIsSaving(false);
    }
  };

  // The field is required: it's what tells us who uses Keepo (the page itself doesn't need it)
  const canAdvance = !!title.trim().length && isCategoryComplete(category, categoryOther) && !isSaving;

  useEffect(() => {
    setAppState({ ...appState, notRedirectOnEmptySites: true } as AppState);
  }, []);

  return (
    <Container>
      {/* where the person is in the flow, on top; the screen's own header under it */}
      <CreationProgress step="name" />
      <ScreenHeader
        title={t("pages.editor.title.title")}
        back={{ label: t("components.appShell.nav.keepos"), to: "/sites" }}
      />
      <Form onSubmit={e => e.preventDefault()}>
        <Section>
          <Card>
            <FormGroup>
              <Input
                type="text"
                id="title"
                maxLength={24}
                required
                value={title}
                onChange={e => setTitle(e.target.value)}
                label={t("pages.editor.title.form.title")}
              />
            </FormGroup>
            {!categoryKnown && (
              <CategoriesInputSelect
                id="category"
                required
                value={category}
                other={categoryOther}
                onChange={(value, other) => {
                  setCategory(value);
                  setCategoryOther(other);
                }}
              />
            )}
            {errorMessage && <p className="error">{errorMessage}</p>}
          </Card>
        </Section>
      </Form>

      {/* Same fixed bar as the editor's save bar */}
      <StickySpacer />
      <Box $sticky>
        <BarContainer>
          <LinkButton onClick={() => navigate("/sites")}>{t("actions.cancel")}</LinkButton>
          <Button
            onClick={() => canAdvance && saveHandler()}
            type="INFO"
            size="LG"
            disabled={!canAdvance}
            $loading={isSaving}>
            {t("pages.editor.title.form.button")}
          </Button>
        </BarContainer>
      </Box>
    </Container>
  );
};

export default TitlePage;
